// @ts-nocheck
import * as _ from 'lodash';
import { DataFilter } from './filters';
import {
  sortDataView,
  getSortColumns,
  containsValue,
  containsFilterFn,
  isEmpty,
} from './helpers';
import {
  CustomDataView,
  SlickDataItem,
  SlickDataView,
  SlickEvent,
  SlickEventHandler,
  SlickGrid,
  SlickGridModel,
  SlickPlugin,
  SlickGridArg,
  SlickGroupItemMetadataProvider,
  DataViewOption,
  SlickCellSelectionModel,
  OnRowsChangedEventArgs as Sg_OnRowsChangedEventArgs,
  OnRowCountChangedEventArgs as Sg_OnRowCountChangedEventArgs,
  Grouping,
  ItemMetadata,
} from 'slickgrid';

export type DataSource<T> = T[] | Function;

export type RowAdapter<T> = (data: T) => T;

interface LocalDataOptions<T> {
  items: DataSource<T>;
  groupBy?: boolean;
  rowAdapter?: RowAdapter<T>;
  noFilter?: boolean;
}

export interface OnDataLoadingEventArgs {}

export interface OnDataLoadedEventArgs {}

export interface OnDataLoadFailedEventArgs {
  error?: Error;
  abort?: boolean;
}

export interface OnRowCountChangedEventArgs {
  total: number;
  visible: number;
}

export interface OnRowsChangedEventArgs {
  total: number;
  visible: number;
}

export class LocalData<T extends object>
  implements CustomDataView<T>, SlickPlugin
{
  pluginName = 'LocalData' as const;

  private readonly eventHandler: SlickEventHandler;
  private readonly dataview: SlickDataView<T>;
  private grid: SlickGridModel<T>;
  private $filter: DataFilter<T>;

  readonly groupItemMetadataProvider: SlickGroupItemMetadataProvider;
  readonly options: LocalDataOptions<T>;
  protected items: DataSource<T>;
  readonly externalRowAdapter: RowAdapter<T>;

  readonly onDataLoading: SlickEvent<OnDataLoadingEventArgs>;
  readonly onDataLoaded: SlickEvent<OnDataLoadedEventArgs>;
  readonly onDataLoadFailed: SlickEvent<OnDataLoadFailedEventArgs>;
  readonly onRowCountChanged: SlickEvent<OnRowCountChangedEventArgs>;
  readonly onRowsChanged: SlickEvent<OnRowsChangedEventArgs>;

  private containsFilter: Function;

  get data() {
    return this.dataview;
  }

  replaceDataSource(ds: any) {
    if (_.isArray(ds)) {
      this.setItems(_.cloneDeep(ds));
    } else if (_.isFunction(ds)) {
      this.items = ds;
      this.reload();
    }
  }

  constructor(options: LocalDataOptions<T>) {
    this.eventHandler = new SlickEventHandler();
    this.onDataLoading = new SlickEvent();
    this.onDataLoaded = new SlickEvent();
    this.onDataLoadFailed = new SlickEvent();
    this.onRowCountChanged = new SlickEvent();
    this.onRowsChanged = new SlickEvent();
    this.options = options;
    this.items = options.items;

    if (_.isFunction(options.rowAdapter)) {
      this.externalRowAdapter = options.rowAdapter;
    }

    const dvOptions: DataViewOption = {};

    if (options.groupBy) {
      // register the group item metadata provider to add expand/collapse group handlers
      this.groupItemMetadataProvider = dvOptions.groupItemMetadataProvider =
        new SlickGroupItemMetadataProvider();
      // dvOptions.inlineFilters = true;
    }

    this.dataview = new SlickDataView(dvOptions);
    this.dataview.getItemMetadata = this.getItemMetadata.bind(this);

    if (options.groupBy) {
      this.installNullGroupRowFilter();
    }
  }

  private installNullGroupRowFilter() {
    const dataview: any = this.dataview;
    const originalGetItem = dataview.getItem.bind(dataview);
    const originalGetItemMetadata = dataview.getItemMetadata.bind(dataview);
    const originalGetIdxById = dataview.getIdxById.bind(dataview);

    let indexMap: number[] = [];
    let reverseMap = new Map<number, number>();
    let lastRowsRef: any = null;
    let lastRowsLen = -1;

    const rebuildIndexMap = () => {
      const rows = dataview.rows as any[] | undefined;
      const len = rows?.length ?? 0;
      if (rows === lastRowsRef && len === lastRowsLen) {
        return;
      }

      const nextMap: number[] = [];
      const nextReverse = new Map<number, number>();
      if (rows) {
        for (let i = 0; i < len; i++) {
          const item = rows[i];
          if (item && item.__group && item.value == null) {
            continue;
          }
          nextReverse.set(i, nextMap.length);
          nextMap.push(i);
        }
      }

      indexMap = nextMap;
      reverseMap = nextReverse;
      lastRowsRef = rows;
      lastRowsLen = len;
    };

    dataview.getLength = () => {
      rebuildIndexMap();
      return indexMap.length;
    };

    dataview.getItem = (i: number) => {
      rebuildIndexMap();
      const realIdx = indexMap[i];
      return realIdx != null ? originalGetItem(realIdx) : undefined;
    };

    dataview.getItemMetadata = (i: number) => {
      rebuildIndexMap();
      const realIdx = indexMap[i];
      return realIdx != null ? originalGetItemMetadata(realIdx) : null;
    };

    dataview.getIdxById = (id: any) => {
      const rawIdx = originalGetIdxById(id);
      if (rawIdx == null) {
        return rawIdx;
      }
      rebuildIndexMap();
      return reverseMap.get(rawIdx);
    };
  }

  init(grid: SlickGridModel<T>) {
    this.grid = grid;
    this.$filter = new DataFilter(grid);

    this.eventHandler
      .subscribe(
        this.dataview.onRowsChanged,
        this.onDataViewRowsChanged.bind(this),
      )
      .subscribe(
        this.dataview.onRowCountChanged,
        this.onDataViewRowCountChanged.bind(this),
      )
      .subscribe(grid.onSort, this.onSort.bind(this));

    this.containsFilter = containsFilterFn(grid);

    // it will unsubscribe after the first call!!!
    const unsubscribe = () => grid.onViewportChanged.unsubscribe(callback);
    const callback = this.onViewportChangedFirst.bind(this, unsubscribe);

    grid.onViewportChanged.subscribe(callback);

    if (this.groupItemMetadataProvider) {
      grid.registerPlugin(this.groupItemMetadataProvider);
      grid.setSelectionModel(new SlickCellSelectionModel());
      grid.setOptions({ enableCellNavigation: true });
    }

    this.dataview.syncGridSelection(grid, true);
  }

  destroy() {
    this.eventHandler.unsubscribeAll();
  }

  getItemMetadata(index: number): ItemMetadata | null {
    const item = this.dataview.rows[index];
    if (item === undefined) {
      return null;
    }

    // overrides for grouping rows
    if ((item as SlickGroup_).__group) {
      return this.dataview._options.groupItemMetadataProvider!.getGroupRowMetadata(
        item as GroupingFormatterItem,
      );
    }

    // overrides for totals rows
    if ((item as SlickGroupTotals_).__groupTotals) {
      return this.dataview._options.groupItemMetadataProvider!.getTotalsRowMetadata(
        item as { group: GroupingFormatterItem },
      );
    }

    const result = _.get(item, '$__itemMetadata__', null);
    if (result) {
      return result;
    }
    return null;
  }

  getItems(visibleOnly: boolean = false) {
    return visibleOnly
      ? _.map(_.range(this.dataview.getLength()), (i) =>
          this.dataview.getItem(i),
        )
      : this.dataview.getItems();
  }

  setItems(items: T[]) {
    if (!items) {
      return;
    }

    _.each(items, (item) => this.rowAdapter(item));

    this.dataview.beginUpdate();
    this.dataview.setItems(items);
    this.dataview.setFilter(this.$filter.callback);
    this.dataview.endUpdate();
    this.applySorters();
  }

  addItem(item: T) {
    this.dataview.beginUpdate();
    this.dataview.addItem(this.rowAdapter(item));
    this.dataview.setFilter(this.$filter.callback);
    this.dataview.endUpdate();
    this.applySorters();
  }

  addItems(items: T[]) {
    this.dataview.beginUpdate();

    for (const item of items) {
      this.dataview.addItem(this.rowAdapter(item));
    }

    this.dataview.setFilter(this.$filter.callback);
    this.dataview.endUpdate();
    this.applySorters();
  }

  updateItem(id: string, item: object) {
    this.rowAdapter(item);
    this.dataview.beginUpdate();
    this.dataview.updateItem(id, item);
    this.dataview.endUpdate();
    this.applySorters();
  }

  removeItem(id: string) {
    this.dataview.beginUpdate();
    this.dataview.deleteItem(id);
    this.dataview.setFilter(this.$filter.callback);
    this.dataview.endUpdate();
    this.applySorters();
  }

  removeItems(ids: string[]) {
    this.dataview.beginUpdate();

    for (const id of ids) {
      this.dataview.deleteItem(id);
    }

    this.dataview.setFilter(this.$filter.callback);
    this.dataview.endUpdate();
    this.applySorters();
  }

  filter(args: Dictionary<string>) {
    this.$filter.setFilters(args);
    this.dataview.refresh();
  }

  groupBy(groupingInfo: Grouping<any> | Grouping<any>[]) {
    this.dataview.beginUpdate();
    this.dataview.setGrouping(groupingInfo);
    this.dataview.endUpdate();
  }

  expandAllGroups() {
    this.dataview.expandAllGroups();
  }

  collapseAllGroups() {
    this.dataview.collapseAllGroups();
  }

  applyFilters() {
    this.dataview.refresh();
  }

  async reload() {
    if (_.isFunction(this.items)) {
      await this.loadItems();
    }
  }

  private async loadItems() {
    this.onDataLoading.notify();

    let items;

    if (_.isFunction(this.items)) {
      try {
        items = await this.items();
      } catch (error) {
        this.onDataLoadFailed.notify({ error });
        return;
      }
    } else {
      items = this.items;
    }

    this.setItems(items);
    this.onDataLoaded.notify();
    return items;
  }

  private onDataViewRowsChanged(e, args: Sg_OnRowsChangedEventArgs) {
    this.grid.invalidateRows(args.rows);
    this.grid.invalidate();
    this.grid.render();

    this.onRowsChanged.notify({
      total: this.dataview.getItems().length,
      visible: args.rows.length,
    });
  }

  private onDataViewRowCountChanged(e, args: Sg_OnRowCountChangedEventArgs) {
    this.grid.updateRowCount();
    this.grid.render();

    this.onRowCountChanged.notify({
      total: this.dataview.getItems().length,
      visible: args.current,
    });
  }

  private onSort(e, args: SlickGridArg<{ grid: SlickGrid }>) {
    const sorters: Slick.SortColumn<T>[] = args.multiColumnSort
      ? args.sortCols
      : [{ sortAsc: args.sortAsc, sortCol: args.sortCol }];

    sortDataView(this.grid, this.dataview, sorters);
  }

  private onViewportChangedFirst(
    unsubscribe: Function,
    e,
    args: OnViewportChangedEventArgs<T>,
  ) {
    unsubscribe();

    if (this.options.groupBy) {
      this.groupBy(this.options.groupBy);
    }

    if (_.isArray(this.options.items)) {
      this.onDataLoading.notify();
      const result = this.setItems(this.options.items);
      this.onDataLoaded.notify();
      return result;
    }

    if (_.isFunction(this.options.items)) {
      return this.loadItems();
    }
  }

  private rowAdapter(data: T) {
    return this.externalRowAdapter ? this.externalRowAdapter(data) : data;
  }

  private applySorters() {
    const sortCols = getSortColumns(this.grid);

    if (sortCols) {
      sortDataView(this.grid, this.dataview, sortCols);
    }
  }
}

// function createGrouping<T>(
//   opts: Slick.Data.GroupingOptions<T>
// ): Grouping<T> {
//   return {
//     getter: opts.getter,
//     formatter:
//       opts.formatter ||
//       createGroupFormatter(_.startCase(opts.getter as string)),
//     comparer: opts.comparer,
//     aggregators: _.map(opts.aggregators, createAggregator),
//     aggregateCollapsed: opts.aggregateCollapsed,
//     collapsed: opts.collapsed
//   };
// }

// const createGroupFormatter = name => g =>
//   `${name}: ${g.value} <span style="color:green"> (${g.count} items)</span>`;

const aggregateRegex = /^(avg|min|max|sum)\(([\w$]+)\)$/g;

/**
 * Instantiates aggregator
 * @param  {String} config {field}:{aggregator} pair. Eg. sum(myfield)
 * @return {Function}        The aggregator instance
 */
function createAggregator<T>(
  config: string | Slick.Data.Aggregators.Aggregator<T>,
): Slick.Data.Aggregators.Aggregator<T> {
  if (_.isString(config)) {
    let m;

    while ((m = aggregateRegex.exec(config)) !== null) {
      // This is necessary to avoid infinite loops with zero-width matches
      if (m.index === aggregateRegex.lastIndex) {
        aggregateRegex.lastIndex++;
      }

      if (m.length === 3) {
        return new Slick.Data.Aggregators[_.startCase(m[1])](m[2]);
      }
    }
    throw new TypeError(
      `createAggregator failed. config: ${config} [slickg.dataview]`,
    );
  }

  return config;
}
